'use client'

import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { ArrowRight, Check, X } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { cn } from '@/lib/utils'
import { type ProductVariant, productStyles } from './product-styles'
import { sectionIconMap, type SectionIconName } from './icon-map'
import layoutStyles from './product-value-prop-section.module.css'

interface Pillar {
  icon: SectionIconName
  title: string
  description: string
}

interface ProductValuePropSectionProps {
  variant: ProductVariant
  eyebrow?: string
  title: string
  subtitle: string
  beforeTitle?: string
  beforeItems: string[]
  afterTitle?: string
  afterItems: string[]
  pillars: Pillar[]
  connected?: boolean
  audience?: ReactNode
}

export function ProductValuePropSection({
  variant,
  eyebrow,
  title,
  subtitle,
  beforeTitle = 'Instead of',
  beforeItems,
  afterTitle = 'You Get',
  afterItems,
  pillars,
  connected = false,
  audience,
}: ProductValuePropSectionProps) {
  const styles = productStyles[variant]
  const pillarContent = pillars.map((item, index) => {
    const Icon = sectionIconMap[item.icon]
    if (!Icon) return null

    return connected ? (
      <li className={layoutStyles.benefitRow} key={item.title}>
        <span className={layoutStyles.benefitIcon} aria-hidden="true">
          <Icon size={20} strokeWidth={1.5} />
        </span>
        <div>
          <h4>{item.title}</h4>
          <p>{item.description}</p>
        </div>
      </li>
    ) : (
      <motion.div
        key={item.title}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: index * 0.1 }}
        className={cn('rounded-2xl p-6 border text-center', styles.card, styles.cardBorder)}
      >
        <div className={cn('inline-flex p-3 rounded-xl mb-4', styles.iconBg, styles.iconColor)}>
          <Icon className="w-6 h-6" />
        </div>
        <h3 className={`text-lg font-semibold ${styles.cardTitle} mb-2`}>{item.title}</h3>
        <p className={`${styles.cardText} text-sm`}>{item.description}</p>
      </motion.div>
    )
  })
  const benefits = (
    <>
      <div className={cn(
        'grid md:grid-cols-2 gap-6 max-w-4xl mx-auto',
        connected && layoutStyles.comparison
      )}>
        {/* Before */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={cn('rounded-2xl p-6 border', styles.card, styles.cardBorder, connected && layoutStyles.before)}
        >
          <div className="flex items-center gap-2 mb-4">
            <X className={`w-5 h-5 ${styles.xColor}`} />
            <h3 className={`text-lg font-semibold ${styles.cardTitle}`}>{beforeTitle}</h3>
          </div>
          <div className="space-y-3">
            {beforeItems.map((item) => (
              <div key={item} className={`flex items-center gap-3 ${styles.cardText}`}>
                <div className={`w-1.5 h-1.5 rounded-full ${styles.xColor}`} />
                {item}
              </div>
            ))}
          </div>
        </motion.div>

        {connected && (
          <div className={layoutStyles.arrow} aria-hidden="true">
            <ArrowRight size={24} strokeWidth={1.5} />
          </div>
        )}

        {/* After */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={cn(
            'rounded-2xl p-6 border bg-gradient-to-br',
            styles.gradientFrom,
            styles.gradientTo,
            styles.accentBorder,
            connected && layoutStyles.after
          )}
        >
          <div className="flex items-center gap-2 mb-4">
            <Check className={`w-5 h-5 ${variant === 'ai' ? 'text-ink' : 'text-white'}`} />
            <h3 className={`text-lg font-semibold ${variant === 'ai' ? 'text-ink' : 'text-white'}`}>{afterTitle}</h3>
          </div>
          {connected ? (
            <ul className={layoutStyles.benefitRows}>{pillarContent}</ul>
          ) : (
            <div className="space-y-3">
              {afterItems.map((item) => (
                <div key={item} className={`flex items-center gap-3 ${variant === 'ai' ? 'text-ink' : 'text-white/90'}`}>
                  <Check className="w-4 h-4 text-current flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {!connected && (
        <div className="grid md:grid-cols-3 gap-6 mt-10">{pillarContent}</div>
      )}
    </>
  )

  return (
    <Section
      background={styles.background}
      className={connected ? layoutStyles.connected : undefined}
    >
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          {eyebrow && (
            <p className={`text-micro-uppercase ${styles.eyebrow} mb-3`}>{eyebrow}</p>
          )}
          <h2 className={cn(
            'text-display-1 mb-4',
            styles.title,
            connected && layoutStyles.title
          )}>{title}</h2>
          <p className={`text-body-lg ${styles.subtitle} max-w-2xl mx-auto`}>
            {subtitle}
          </p>
        </motion.div>

        {connected ? (
          <div className={layoutStyles.panel}>
            {audience && <div className={layoutStyles.audience}>{audience}</div>}
            {benefits}
          </div>
        ) : (
          <>
            {audience}
            {benefits}
          </>
        )}
      </Container>
    </Section>
  )
}
